import { ResultData } from "../types";

// Use "/api" if you set up the Vite proxy, otherwise the full URL
//const API_BASE = "/api";
const API_BASE = "https://staging.webapi.eraay.in";

export interface GetScoreCardParams {
    id: string;
    token: string;
    index: string;
}

export async function getScoreCardInfo(
    { id, token, index }: GetScoreCardParams,
    signal?: AbortSignal
): Promise<ResultData> {
    const url = `${API_BASE}/GetScoreCardInfo/${encodeURIComponent(
        id
    )}/${encodeURIComponent(token)}/${encodeURIComponent(index)}`;

    const res = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
        signal,
    });

    if (!res.ok) {
        throw new Error(`API error: ${res.status} ${res.statusText}`);
    }

    const json = await res.json();

    // Handle common wrappers: { data: {...} }, { result: {...} }, or flat
    const payload = json?.data ?? json?.result ?? json;

    return mapApiResponseToResultData(payload, id);
}

function mapApiResponseToResultData(json: any, id: string): ResultData {
    // 1. Unwrap "Default" (and other common wrappers)
    let payload =
        json?.Default ??
        json?.default ??
        json?.data ??
        json?.result ??
        json;

    // 2. If it's an array, take the first item
    if (Array.isArray(payload)) {
        payload = payload[0];
    }

    // 3. If still nested (e.g. { Default: [ {...} ] } handled above; safety net)
    if (Array.isArray(payload?.Default)) {
        payload = payload.Default[0];
    }

    if (!payload) {
        throw new Error("Empty response from API");
    }

    const totalScore = Number(payload?.TotalScore ?? 0);
    const totalScoreSecured = Number(payload?.TotalScoreSecured ?? 0);
    const percentageScored = Number(payload?.PercentageScored ?? 0);

    const status: ResultData["status"] =
        percentageScored >= 40 ? "PASSED" : "FAILED";

    return {
        niesbudRegNo: String(payload?.NiesbudRegNo ?? ""),
        candidateName: String(payload?.CandidateName ?? ""),
        candidateEmail: String(payload?.CandidateEmail ?? ""),
        candidatePhone: String(payload?.CandidatePhone ?? ""),
        totalScore,
        totalScoreSecured,
        percentageScored,
        candidateFatherName: String(payload?.CandidateFatherName ?? ""),
        programName :String(payload?.ProgramName ?? ""),
        batchName: String(payload?.BatchName ?? ""),
        id,
        status,
    };
}

// Add these interfaces
export interface QuestionDetail {
    QuestionNumber: string;
    Question: string;

    Code: string;
    Description: string;
    ShadowQno: string;
    CorrectAnswer: string;
    GivenAnswer: string;
    Result: string;
}

export interface ScoreCardDetails {
    questions: QuestionDetail[];
    niesbudRegNo: string;
    candidateName: string;
    // Add other fields as needed
}

// Add this function
export async function getScoreCardDetails(
    { id, token, index }: GetScoreCardParams,
    signal?: AbortSignal
): Promise<QuestionDetail[]> {
    const url = `${API_BASE}/GetScoreCardDetails/${encodeURIComponent(id)}/${encodeURIComponent(token)}`;

    const res = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
        signal,
    });

    if (!res.ok) {
        throw new Error(`API error: ${res.status} ${res.statusText}`);
    }

    const json = await res.json();

    // Handle the Default1 array format
    let payload = json?.Default1 ?? json?.Default ?? json?.data ?? json;

    if (!Array.isArray(payload)) {
        payload = [payload];
    }

    return payload as QuestionDetail[];
}